/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: PCG timing pattern / rhythm regularity classification and labels.
// Source: cardiac_report_v0_5_6_dev_2.html lines 3429-3575 (label suite),
// 3362-3375 (timingSummaryResultLabel via display), 5368-5407 (provider method text).
// Open design question: this suite re-derives timing pattern labels from
// evidence numbers/strings the viewer (pcg-core timing.js) already classified;
// which module should own the labels is undecided.
// Deviation: observed text in timing-variation wording ("±N% timing variation
// · Moderate") is graded by the label it ends with. The prototype reads its
// leading number as the older percent-regular figure, which turns every
// Moderate card's suggested interpretation into "Elevated.".
// Heart Report v0.5.8 at cardiac-panel-v4 git 94432d0: a Cycle Regularity
// card's quoted ± timing swing decides its classification and its marker —
// the swing against the card's own bands (`rhythmRegularityThresholds`: the
// imported expected / irregular swing, else ±8% / ±18%) — so the figure, the
// label and the marker always agree. Deviations: a missing swing stays
// missing (upstream reads a null swing as 0, an "expected" result); and once
// the reviewer has set the card's result, the label they set stands — the
// imported swing no longer speaks for the card.

import { parseLeadingNumber } from "./parse"

export function normalizePcgTimingPatternLabel(value) {
  const text = String(value || "")
    .trim()
    .toLowerCase()
  if (!text) return ""
  if (text.includes("periodic")) return "Periodic PCG timing pattern"
  if (text.includes("low pcg") || text.includes("low timing")) return "Expected PCG timing variability"
  if (text.includes("expected")) return "Expected PCG timing variability"
  if (text.includes("normal")) return "Expected PCG timing variability"
  if (text.includes("high pcg") || text.includes("markedly increased")) return "High PCG timing variability"
  if (text.includes("moderate pcg") || text.includes("increased pcg") || text.includes("increased timing"))
    return "Moderate PCG timing variability"
  if (text.includes("regularly irregular")) return "Regularly irregular"
  if (text.includes("borderline variable")) return "Moderate PCG timing variability"
  if (text.includes("borderline irregular")) return "Moderate PCG timing variability"
  if (text.includes("borderline") && text.includes("timing")) return "Moderate PCG timing variability"
  if (text.includes("elevated") && text.includes("timing")) return "High PCG timing variability"
  if (text.includes("irregularly irregular")) return "High PCG timing variability"
  if (text.includes("irregular")) return "High PCG timing variability"
  if (text.includes("variable")) return "Moderate PCG timing variability"
  if (text.includes("regular")) return "Expected PCG timing variability"
  return ""
}

export function pcgTimingDisplayLabel(label) {
  const normalized = normalizePcgTimingPatternLabel(label) || String(label || "")
  if (normalized === "Expected PCG timing variability") return "Normal"
  if (normalized === "Moderate PCG timing variability") return "Moderate"
  if (normalized === "High PCG timing variability") return "Elevated"
  if (normalized === "Periodic PCG timing pattern") return "Periodic"
  if (String(normalized).toLowerCase().includes("limited")) return "N/A"
  return normalized
}

export function timingPatternFromRegularityInputs(
  closePct,
  cycleVariability,
  markerConfidence,
  timingVariabilityPct,
  expectedNormalVariabilityPct,
  irregularVariabilityPct,
  timingSwingPct,
  expectedNormalSwingPct,
  irregularSwingPct
) {
  // A missing metric stays missing: Number(null) is 0, which would read as a
  // real (and always "expected") variability value.
  const optionalNumber = (value) => (value === null || value === undefined || value === "" ? NaN : Number(value))
  const timingVar = optionalNumber(timingSwingPct)
  const expected = optionalNumber(expectedNormalSwingPct)
  const irregular = optionalNumber(irregularSwingPct)
  const conf = optionalNumber(markerConfidence)
  if (Number.isFinite(timingVar) && Number.isFinite(expected) && Number.isFinite(irregular)) {
    if (timingVar <= expected) return "Expected PCG timing variability"
    if (timingVar > irregular && (!Number.isFinite(conf) || conf >= 0.75)) return "High PCG timing variability"
    return "Moderate PCG timing variability"
  }
  const legacyTimingVar = optionalNumber(timingVariabilityPct)
  const legacyExpected = optionalNumber(expectedNormalVariabilityPct)
  const legacyIrregular = optionalNumber(irregularVariabilityPct)
  if (Number.isFinite(legacyTimingVar) && Number.isFinite(legacyExpected) && Number.isFinite(legacyIrregular)) {
    if (legacyTimingVar <= legacyExpected) return "Expected PCG timing variability"
    if (legacyTimingVar > legacyIrregular && (!Number.isFinite(conf) || conf >= 0.75))
      return "High PCG timing variability"
    return "Moderate PCG timing variability"
  }
  const close = optionalNumber(closePct)
  const cv = optionalNumber(cycleVariability)
  if (Number.isFinite(close) && Number.isFinite(cv)) {
    if (close >= 85 && cv <= 0.08) return "Expected PCG timing variability"
    if (cv <= 0.22 || close >= 50 || (Number.isFinite(conf) && conf < 0.75)) return "Moderate PCG timing variability"
    return "High PCG timing variability"
  }
  if (Number.isFinite(close)) {
    if (close >= 85) return "Expected PCG timing variability"
    if (close >= 50) return "Moderate PCG timing variability"
    return "High PCG timing variability"
  }
  return ""
}

// Observed text in timing-variation wording ("±8.3% timing variation ·
// Moderate"). Its number is a deviation (small = steady), so it must never be
// graded as the older percent-regular figure (large = steady); the pattern is
// the display label the text ends with, when it has one.
// The cycle-CV fallback wording ("Median cycle CV 5.2% · review") carries a
// coefficient of variation, another figure that is never a percent-regular
// value; it is graded like timing-variation text.
export function isTimingVariationText(value) {
  const text = String(value ?? "").trim()
  return text.startsWith("±") || /^median cycle cv\b/i.test(text)
}

function timingVariationTextPattern(value) {
  const text = String(value ?? "")
  const label = text.includes("·")
    ? text
        .slice(text.lastIndexOf("·") + 1)
        .trim()
        .toLowerCase()
    : ""
  if (label === "normal") return "Expected PCG timing variability"
  if (label === "moderate") return "Moderate PCG timing variability"
  if (label === "elevated") return "High PCG timing variability"
  if (label === "periodic") return "Periodic PCG timing pattern"
  return ""
}

const SWING_TEXT_PATTERN = /±\s*(\d+(?:\.\d+)?)\s*%/

/**
 * The ± timing swing a Cycle Regularity card quotes, in percent: its
 * structured `timingSwingPct`, else the figure in its "±N% timing variation"
 * observed text; null when it quotes none. Also reads a bare observed text.
 */
export function rhythmRegularitySwingPct(featureOrObserved) {
  if (featureOrObserved && typeof featureOrObserved === "object") {
    const structured = featureOrObserved.timingSwingPct
    if (structured !== null && structured !== undefined && structured !== "" && Number.isFinite(Number(structured))) {
      return Number(structured)
    }
    const observedMatch = SWING_TEXT_PATTERN.exec(String(featureOrObserved.observed || ""))
    return observedMatch ? Number(observedMatch[1]) : null
  }
  const textMatch = SWING_TEXT_PATTERN.exec(String(featureOrObserved || ""))
  return textMatch ? Number(textMatch[1]) : null
}

/**
 * The swing bands a Cycle Regularity card is graded on: its imported ones,
 * else the viewer's rule for the heart rate — Normal up to ±8% (±11% below
 * 60 bpm), Moderate to ±18%, Elevated above.
 */
export function rhythmRegularityThresholds(feature, hrBpm) {
  const normal = Number(feature?.expectedNormalSwingPct)
  const elevated = Number(feature?.irregularSwingPct)
  const rate = Number(hrBpm)
  const normalBand = Number.isFinite(normal) && normal > 0 ? normal : Number.isFinite(rate) && rate < 60 ? 11 : 8
  return {
    normal: normalBand,
    elevated: Number.isFinite(elevated) && elevated > normalBand ? elevated : 18,
  }
}

/** The pattern a swing stands for on the given bands. */
export function rhythmRegularitySwingLabel(swing, thresholds) {
  if (swing <= thresholds.normal) return "Expected PCG timing variability"
  if (swing <= thresholds.elevated) return "Moderate PCG timing variability"
  return "High PCG timing variability"
}

/** A Cycle Regularity value in the card's wording: "±6.0% timing variation · Normal". */
export function rhythmRegularitySwingText(swing, patternLabel) {
  const display = pcgTimingDisplayLabel(patternLabel)
  const labelSuffix = display ? ` · ${display}` : ""
  return `±${Number(swing).toFixed(1)}% timing variation${labelSuffix}`
}

// The pattern a card's quoted swing stands for on the card's own bands; ""
// when it quotes none, or once the reviewer has set the card's result.
function swingPatternLabel(feature) {
  const swing = feature.reviewTouched ? null : rhythmRegularitySwingPct(feature)
  if (swing === null) return ""
  return rhythmRegularitySwingLabel(swing, rhythmRegularityThresholds(feature))
}

export function rhythmRegularityPatternLabel(featureOrObserved, fallbackStatus) {
  if (featureOrObserved && typeof featureOrObserved === "object") {
    // The quoted swing classifies the card until the reviewer sets a result
    const fromSwing = swingPatternLabel(featureOrObserved)
    if (fromSwing) return fromSwing
    const explicit = normalizePcgTimingPatternLabel(
      featureOrObserved.timingPatternLabel || featureOrObserved.rhythmPattern
    )
    if (explicit) return explicit
    const swingText = isTimingVariationText(featureOrObserved.observed)
    const pct = Number(
      featureOrObserved.regularityPct ?? (swingText ? NaN : parseLeadingNumber(featureOrObserved.observed))
    )
    const cv = Number(featureOrObserved.cycleVariability)
    const conf = Number(featureOrObserved.markerConfidence ?? featureOrObserved.confidence)
    return (
      timingPatternFromRegularityInputs(
        pct,
        cv,
        conf,
        featureOrObserved.timingVariabilityPct,
        featureOrObserved.expectedNormalVariabilityPct,
        featureOrObserved.irregularVariabilityPct,
        featureOrObserved.timingSwingPct,
        featureOrObserved.expectedNormalSwingPct,
        featureOrObserved.irregularSwingPct
      ) || (swingText ? timingVariationTextPattern(featureOrObserved.observed) : "")
    )
  }
  const explicit = normalizePcgTimingPatternLabel(featureOrObserved)
  if (explicit) return explicit
  if (isTimingVariationText(featureOrObserved)) {
    return timingVariationTextPattern(featureOrObserved) || normalizePcgTimingPatternLabel(fallbackStatus)
  }
  const pct = parseLeadingNumber(featureOrObserved)
  const inferred = timingPatternFromRegularityInputs(pct, null, null, null, null, null)
  if (inferred) return inferred
  return normalizePcgTimingPatternLabel(fallbackStatus)
}

/** The pattern label a reviewer-set status stands for (marker drag / typed value). */
export function timingPatternLabelForStatus(status) {
  if (status === "green") return "Expected PCG timing variability"
  if (status === "red") return "High PCG timing variability"
  return "Moderate PCG timing variability"
}

export function rhythmRegularityStatusFromPattern(patternLabel, fallbackStatus) {
  const label = normalizePcgTimingPatternLabel(patternLabel)
  if (label === "Expected PCG timing variability") return "green"
  if (label === "High PCG timing variability") return "red"
  if (label) return "amber"
  return fallbackStatus || "green"
}

export function rhythmRegularityObservedText(incoming) {
  if (!incoming) return null
  const observed = incoming.observed !== undefined && incoming.observed !== null ? String(incoming.observed) : ""
  const swing = Number(incoming.timingSwingPct)
  const label = rhythmRegularityPatternLabel(
    {
      timingPatternLabel: incoming.timingPatternLabel,
      rhythmPattern: incoming.rhythmPattern,
      regularityPct: incoming.regularityPct,
      cycleVariability: incoming.cycleVariability,
      timingVariabilityPct: incoming.timingVariabilityPct ?? incoming.robustSpreadPct,
      timingSwingPct: incoming.timingSwingPct,
      expectedNormalVariabilityPct: incoming.expectedNormalVariabilityPct,
      expectedNormalSwingPct: incoming.expectedNormalSwingPct,
      irregularVariabilityPct: incoming.irregularVariabilityPct,
      irregularSwingPct: incoming.irregularSwingPct,
      selectedCycleCount: incoming.selectedCycleCount,
      rawMarkerConfidence: incoming.rawMarkerConfidence,
      timingCycleConfidence: incoming.timingCycleConfidence,
      markerConfidence: incoming.markerConfidence ?? incoming.confidence,
      observed,
    },
    incoming.status
  )
  const displayLabel = pcgTimingDisplayLabel(label)
  if (Number.isFinite(swing)) {
    const base = `±${swing.toFixed(1)}% timing variation`
    return displayLabel ? `${base} · ${displayLabel}` : base
  }
  if (!displayLabel) return observed ? observed.replace(/Expected PCG timing variability/g, "Normal") : null
  const cleanedObserved = observed.replace(/Expected PCG timing variability/g, "Normal")
  if (cleanedObserved && cleanedObserved.toLowerCase().includes(displayLabel.toLowerCase())) return cleanedObserved
  return cleanedObserved ? `${cleanedObserved} · ${displayLabel}` : displayLabel
}

export function rhythmRegularityColorClass(observed, fallbackStatus, hrBpm) {
  const patternLabel = rhythmRegularityPatternLabel(observed, fallbackStatus)
  if (patternLabel) return rhythmRegularityStatusFromPattern(patternLabel, fallbackStatus)
  // Timing-variation wording without a label is graded on the swing bands
  // for the heart rate; its number is never read as a percent-regular figure
  if (typeof observed === "string" && isTimingVariationText(observed)) {
    const swing = rhythmRegularitySwingPct(observed)
    if (swing !== null) {
      return rhythmRegularityStatusFromPattern(
        rhythmRegularitySwingLabel(swing, rhythmRegularityThresholds(null, hrBpm)),
        fallbackStatus
      )
    }
  }
  const obsValue = isTimingVariationText(observed) ? null : parseLeadingNumber(observed)
  if (Number.isFinite(obsValue)) {
    if (obsValue >= 85) return "green"
    if (obsValue >= 50) return "amber"
    return "red"
  }
  if (["green", "amber", "red", "grey"].includes(fallbackStatus)) return fallbackStatus
  const statusText = String(fallbackStatus || "").toLowerCase()
  if (statusText.includes("mostly") || statusText.includes("normal")) return "green"
  if (statusText.includes("variable") || statusText.includes("borderline")) return "amber"
  if (statusText.includes("irregular") || statusText.includes("elevated")) return "red"
  return "green"
}

export function timingSummaryResultLabel(colorClass) {
  if (colorClass === "red") return "High PCG timing variability"
  if (colorClass === "amber") return "Moderate PCG timing variability"
  return "Normal"
}

export function rhythmRegularityDisplayLabel(feature, colorClass) {
  const pattern = rhythmRegularityPatternLabel(feature, colorClass)
  if (pattern) return pcgTimingDisplayLabel(pattern)
  if (colorClass === "green") return "Normal"
  if (colorClass === "amber") return "Moderate"
  if (colorClass === "red") return "Elevated"
  return "Normal"
}

export function rhythmRegularityProviderMethodText(observed) {
  const text = String(observed || "")
  const swingMatch = text.match(/±\s*([0-9]+(?:\.[0-9]+)?)%/)
  if (swingMatch) {
    const swing = Number(swingMatch[1])
    if (!Number.isFinite(swing) || swing <= 0) {
      return "Usable S1-to-S1 timing category was available; numeric timing spread was not available."
    }
    return `Approximate S1-to-S1 timing variation was ±${swingMatch[1]}% across selected usable cycles.`
  }
  const pct = parseLeadingNumber(observed)
  if (pct === null) {
    return "Usable S1-to-S1 timing evidence was not available."
  }
  return `${pct}% of usable S1-to-S1 cycles were within 10% of the median cycle length.`
}

export function rhythmRegularityProviderMethodFromFeature(feature) {
  const swingPct = Number(feature?.timingSwingPct)
  const swingMs = Number(feature?.timingSwingMs)
  const medianCycleMs = Number(feature?.medianCycleMs)
  const method = feature?.metricUsed ? ` Metric: ${feature.metricUsed}.` : ""
  if (Number.isFinite(swingPct) && Number.isFinite(swingMs) && Number.isFinite(medianCycleMs) && medianCycleMs > 0) {
    return `Approximate S1-to-S1 timing variation was ±${swingPct.toFixed(1)}%, about ±${swingMs.toFixed(
      0
    )} ms around a median cycle length of ${medianCycleMs.toFixed(0)} ms.${method}`
  }
  return `${rhythmRegularityProviderMethodText(feature?.observed)}${method}`
}
